"use client"

import React, { useState, useEffect } from "react"
import styled from "styled-components"
import PostCard from "./PostCard"
import { TrendingUp, Loader, Filter, Grid, List } from "lucide-react"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`

const Header = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 20px;
`

const Title = styled.h1`
  font-size: 24px;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`

const Description = styled.p`
  color: #666;
  margin: 0;
`

const FilterSection = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FilterButtons = styled.div`
  display: flex;
  gap: 8px;
`

const FilterButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.$active ? '#2563eb' : '#e5e7eb'};
  background-color: ${props => props.$active ? '#2563eb' : 'white'};
  color: ${props => props.$active ? 'white' : '#374151'};
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.$active ? '#1d4ed8' : '#f9fafb'};
    border-color: ${props => props.$active ? '#1d4ed8' : '#d1d5db'};
  }
`

const ViewToggle = styled.div`
  display: flex;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
`

const ViewButton = styled.button`
  padding: 8px 12px;
  border: none;
  background-color: ${props => props.$active ? '#2563eb' : 'white'};
  color: ${props => props.$active ? 'white' : '#6b7280'};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: ${props => props.$active ? '#2563eb' : '#f9fafb'};
  }
`

const PostsContainer = styled.div`
  display: ${props => props.$viewMode === 'grid' ? 'grid' : 'flex'};
  ${props => props.$viewMode === 'grid' 
    ? `
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 20px;
    ` 
    : `
      flex-direction: column;
      gap: 20px;
    `}
`

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #ef4444;
  background-color: #fee2e2;
  border-radius: 8px;
`

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f9fafb;
    border-color: #d1d5db;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export default function RecommendedPage() {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [filter, setFilter] = useState('all') // all, today, week, month
  const [viewMode, setViewMode] = useState('list')
  const [loadingMore, setLoadingMore] = useState(false)

  const fetchRecommendedPosts = async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) {
        setLoading(true)
      } else {
        setLoadingMore(true)
      }
      setError(null)
      
      const response = await fetch(`/post/recommended-posts/more?page=${pageNum}&limit=12`, {
        credentials: 'include'
      })
      
      const data = await response.json()
      
      if (data.success) {
        console.log("추천 게시물 로드 성공:", data.posts)
        
        if (append) {
          setPosts(prev => [...prev, ...data.posts])
        } else {
          setPosts(data.posts)
        }
        
        // 더 불러올 게시물이 있는지 확인
        if (data.pagination) {
          setHasMore(pageNum < data.pagination.totalPages)
        } else {
          setHasMore(data.posts.length === 12)
        }
      } else {
        console.error("추천 게시물 로드 실패:", data.message)
        setError(data.message || "게시물을 불러오는데 실패했습니다.")
      }
    } catch (error) {
      console.error("추천 게시물 로드 오류:", error)
      setError("서버 연결에 문제가 발생했습니다.")
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    fetchRecommendedPosts(1, false)
  }, [filter])

  const handleLoadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    fetchRecommendedPosts(nextPage, true)
  }

  const filterPosts = () => {
    if (filter === 'all') return posts
    
    const now = new Date()
    const filtered = posts.filter(post => {
      const postDate = new Date(post.created_at)
      const diffTime = Math.abs(now - postDate)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      switch(filter) {
        case 'today':
          return diffDays <= 1
        case 'week':
          return diffDays <= 7
        case 'month':
          return diffDays <= 30
        default:
          return true
      }
    })
    
    return filtered
  }

  const displayPosts = filterPosts()

  return (
    <Container>
      <Header>
        <Title>
          <TrendingUp size={24} />
          추천 게시물
        </Title>
        <Description>
          인기 있는 게시물들을 확인해보세요. 좋아요와 북마크가 많은 게시물들을 추천해드립니다.
        </Description>
      </Header>

      <FilterSection>
        <FilterButtons>
          <FilterButton 
            $active={filter === 'all'} 
            onClick={() => setFilter('all')}
          >
            전체
          </FilterButton>
          <FilterButton 
            $active={filter === 'today'} 
            onClick={() => setFilter('today')}
          >
            오늘
          </FilterButton>
          <FilterButton 
            $active={filter === 'week'} 
            onClick={() => setFilter('week')}
          >
            이번 주
          </FilterButton>
          <FilterButton 
            $active={filter === 'month'} 
            onClick={() => setFilter('month')}
          >
            이번 달
          </FilterButton>
        </FilterButtons>
        
        <ViewToggle>
          <ViewButton 
            $active={viewMode === 'list'} 
            onClick={() => setViewMode('list')}
          >
            <List size={16} />
          </ViewButton>
          <ViewButton 
            $active={viewMode === 'grid'} 
            onClick={() => setViewMode('grid')}
          >
            <Grid size={16} />
          </ViewButton>
        </ViewToggle>
      </FilterSection>

      {loading ? (
        <LoadingIndicator>
          <Loader size={20} className="animate-spin" />
          <span>추천 게시물을 불러오는 중...</span>
        </LoadingIndicator>
      ) : error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : displayPosts.length === 0 ? (
        <LoadingIndicator>추천할 게시물이 없습니다.</LoadingIndicator>
      ) : (
        <>
          <PostsContainer $viewMode={viewMode}>
            {displayPosts.map(post => {
              // 이미지 처리
              let processedImages = []
              if (post.images) {
                let images = post.images
                if (typeof images === 'string') {
                  try {
                    images = JSON.parse(images)
                  } catch (e) {
                    images = []
                  }
                }
                if (Array.isArray(images)) {
                  processedImages = images.map(img => 
                    typeof img === 'object' ? (img.url || img.image_url) : img
                  ).filter(url => url)
                }
              }
              
              return (
                <PostCard 
                  key={post.id} 
                  post={{
                    id: post.id,
                    author: post.author_username,
                    authorAvatar: post.author_avatar,
                    timestamp: post.created_at,
                    images: processedImages,
                    tags: post.tags || [],
                    description: post.content,
                    like_count: post.like_count,
                    comment_count: post.comment_count,
                    bookmark_count: post.bookmark_count
                  }}
                  onPostDeleted={() => fetchRecommendedPosts(1, false)}
                  onPostUpdated={() => fetchRecommendedPosts(1, false)}
                />
              )
            })}
          </PostsContainer>
          
          {hasMore && !loadingMore && (
            <LoadMoreButton onClick={handleLoadMore}>
              더 보기
            </LoadMoreButton>
          )}
          
          {loadingMore && (
            <LoadingIndicator>
              <Loader size={20} className="animate-spin" />
              <span>더 불러오는 중...</span>
            </LoadingIndicator>
          )}
        </>
      )}
    </Container>
  )
}